import { useState, useEffect } from 'react';
import { Pagination } from '../../../../../model/pagination';
import { IFileResponse } from '../../../../../model/scp/base/response/file-response';
import { IFilePageRequest } from '../../../../../model/scp/base/request/file-paged-request';
import { IFileAddRequest } from '../../../../../model/scp/base/request/file-add-request';
import { ITableResponse } from '../../../../../model/scp/purchase/response/tables-response';
import BaseAppService from '../../../../../application/platform/scp/base/base-app-service';
import PurchaseAppService from '../../../../../application/platform/scp/purchase/purchase-app-service';
import { useBaseLayout } from '../../../../../components/context/use-base-layout';

export const usePurchaseFileController = (purchaseId?: string) => {
    const { showDialog, showMessage, showLoading } = useBaseLayout();
    const [filePurchaseRequest, setFilePurchaseRequest] = useState<IFileAddRequest>({ id: purchaseId } as IFileAddRequest);
    const [purchaseFilesResponse, setPurchaseFilesResponse] = useState<Pagination<IFileResponse>>({ items: [] } as unknown as Pagination<IFileResponse>);
    const [purchaseFilesRequest, setPurchaseFilesRequest] = useState<IFilePageRequest>({ id: purchaseId, page: 1, pageSize: 100 } as IFilePageRequest);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const [error, setError] = useState(false);
    const [typeDocument, setTypeDocument] = useState<ITableResponse[]>([]);

    useEffect(() => {
        loadTypeDocuments();
    }, []);

    useEffect(() => {
        loadData();
    }, [purchaseId]);

    const loadTypeDocuments = async () => {
        try {
            const documentTypes = await BaseAppService.listTypeDocument(2);
            setTypeDocument(documentTypes);
        } catch (error) {
            showMessage({
                open: true,
                message: 'Não foi possível carregar os tipos de documento',
                detail: (error as any).details || (error as Error).message,
                type: 'error'
            });
        }
    };

    const loadData = async () => {
        if (purchaseFilesRequest.id !== undefined) {
            try {
                setLoadMoreData(true);
                const purchaseFiles = await PurchaseAppService.listPurchaseFiles(purchaseFilesRequest);
                setPurchaseFilesResponse(purchaseFiles);
                setLoadMoreData(false);
            } catch (error) {
                showMessage({
                    open: true,
                    message: 'Não foi possível carregar os arquivos',
                    detail: (error as any).details || (error as Error).message,
                    type: 'error'
                });
                setLoadMoreData(false);
            }
        }
    };

    const handleOnChange = (e: any) => {
        let title = filePurchaseRequest?.title;
        let documentTypeId = filePurchaseRequest?.documentTypeId;

        if (e !== undefined) {
            if (e.target.name === "input-title-atachment") {
                title = e.target.value;
            } else if (e.target.name === "select-document-type-atachment") {
                documentTypeId = e.target.value;
            }
        }

        setFilePurchaseRequest(prevState => {
            return { ...prevState, title, documentTypeId };
        });
    };

    const validFields = (): boolean => {
        let isValid = true;

        if (filePurchaseRequest.documentTypeId === undefined) {
            isValid = false;
        }
        if (filePurchaseRequest.document !== undefined && filePurchaseRequest.document === null) {
            isValid = false;
        }
        if (filePurchaseRequest.title !== undefined && filePurchaseRequest.title.length === 0) {
            isValid = false;
        }

        setError(!isValid);
        return isValid;
    };

    const saveFile = async () => {
        if (validFields()) {
            try {
                showLoading({ open: true });
                await PurchaseAppService.addFilePurchase(filePurchaseRequest);
                showMessage({ open: true, message: "Documento adicionado com sucesso!", type: "success" });
                setFilePurchaseRequest({ id: purchaseId } as IFileAddRequest);
                showLoading({ open: false });
                await loadData();
            } catch (error) {
                showLoading({ open: false });
                showMessage({
                    open: true,
                    message: 'Não foi possível adicionar o arquivo',
                    detail: (error as any).details || (error as Error).message,
                    type: 'error'
                });
            }
        } else {
            showMessage({ open: true, message: "Preencha todos os campos obrigatórios!", type: "error" });
        }
    };

    const deleteFile = async (sequence: number, justification: string) => {
        if (justification !== undefined && justification.length > 0) {
            if (purchaseId !== undefined) {
                try {
                    showLoading({ open: true });
                    await PurchaseAppService.deleeteFilePurchase(justification, Number(purchaseId), sequence);
                    await loadData();
                    showLoading({ open: false });
                    showMessage({ message: 'Anexo excluído com sucesso', type: 'success', open: true });
                } catch (error) {
                    showLoading({ open: false });
                    showMessage({
                        open: true,
                        message: 'Não foi possível excluir o arquivo',
                        detail: (error as any).details || (error as Error).message,
                        type: 'error'
                    });
                }
            }
        } else {
            showMessage({ message: 'Informe a justificativa para excluir o anexo', type: 'warning', open: true });
        }
    };

    const setFileUpload = (file: File) => {
        setFilePurchaseRequest({ ...filePurchaseRequest, document: file });
    };

    const handlePageChange = (newPage: number) => {
        setPurchaseFilesRequest({ ...purchaseFilesRequest, page: newPage });
    };

    return {
        // State
        filePurchaseRequest,
        purchaseFilesResponse,
        loadMoreData,
        error,
        typeDocument,

        // Methods
        handleOnChange,
        saveFile,
        deleteFile,
        setFileUpload,
        handlePageChange,
        showDialog,
        showLoading,
        showMessage
    };
};
